import {
  CHART_CONFIGURATION_FIELDS,
  RESULT_TYPE_FIELDS,
  USER_DATAPOINT_FIELDS_FOR_COT,
  USER_RESULT_FIELDS_FOR_COT,
  USER_RESULT_FIELDS_TRUNCATED,
  USER_TEST_FIELDS_TRUNCATED
} from "@/graphql/tpo/results/types";
import { gql } from "@apollo/client";

export const LAB_REPORTS_QUERY = gql`
  query LabReportsQuery(
    $userTestId: ID!
    $userId: ID!
    $forPractitioner: Boolean
    $organisationId: ID!
  ) {
    userTest(userTestId: $userTestId, userId: $userId, organisationId: $organisationId) {
      id
      generatedPdf {
        id
        name
        userTest {
          id
        }
        pdfFile
      }
      dataFiles(forPractitioner: $forPractitioner) {
        id
        name
        dataFileUrl
        testDataFile {
          id
          fileType {
            id
            name
            contentType
            extension
          }
        }
      }
    }
  }
`;

export const USER_RESULTS_FOR_USER_TEST_QUERY = gql`
  query UserResultsForUserTestQuery(
    $userId: ID!
    $userTestId: ID!
    $first: Int!
    $after: String
    $orderBy: String
    $displayGroup: String
    $organisationId: ID!
  ) {
    userResultConnections(
      userId: $userId
      userTestId: $userTestId
      organisationId: $organisationId
      after: $after
      first: $first
      orderBy: $orderBy
      displayGroup: $displayGroup
    ) {
      edges {
        cursor
        node {
          id
          isGenesRelated
          result {
            id
            name
            slug
            description
            function
            reason
            chartType
          }
          chartConfiguration {
            ...ChartConfigurationFields
          }
          resultState {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${CHART_CONFIGURATION_FIELDS}
`;

export const TEST_RESULTS_QUERY = gql`
  query TestResultsQuery(
    $userId: ID!
    $userTestId: ID!
    $first: Int!
    $after: String
    $orderBy: String
    $displayGroup: String
    $organisationId: ID!
  ) {
    userTest(userId: $userId, userTestId: $userTestId, organisationId: $organisationId) {
      ...UserTestFieldsTruncated
      userResultConnections(
        after: $after
        first: $first
        orderBy: $orderBy
        displayGroup: $displayGroup
      ) {
        edges {
          cursor
          node {
            id
            isGenesRelated
            result {
              id
              name
              slug
              description
              function
              reason
              chartType
            }
            chartConfiguration {
              ...ChartConfigurationFields
            }
            resultState {
              id
              name
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
  ${USER_TEST_FIELDS_TRUNCATED}
  ${CHART_CONFIGURATION_FIELDS}
`;

export const USER_RESULT_QUERY = gql`
  query UserResultQuery($userId: ID!, $userResultId: ID!, $organisationId: ID!) {
    userResult(userId: $userId, userResultId: $userResultId, organisationId: $organisationId) {
      id
      isGenesRelated
      value
      freshValue
      interpretation
      availableDatapointRatio
      result {
        ...ResultTypeFields
      }
      thingsToDoCount
      thingsToAvoidCount
      peopleToSeeCount
      thingsToTakeCount
      peopleToSeeCount
      chartConfiguration {
        ...ChartConfigurationFields
      }
      resultState {
        id
        name
        description
      }
    }
  }
  ${RESULT_TYPE_FIELDS}
  ${CHART_CONFIGURATION_FIELDS}
`;

export const USER_DATAPOINT_QUERY = gql`
  query USER_DATAPOINT_QUERY($userDatapointId: ID!, $userId: ID!, $organisationId: ID!) {
    userDatapoint(
      userDatapointId: $userDatapointId
      userId: $userId
      organisationId: $organisationId
    ) {
      id
      uploaded
      dateExpired
      datapoint {
        id
        name
        category
        units
        description
        function
        reason
        chartType
        testProductSet {
          id
          paymentIsoCode
          organisation
          productFamily {
            id
            name
            slug
          }
        }
      }
      riskAllele
      value
      interpretation
      riskAllele
      chartConfiguration {
        ...ChartConfigurationFields
      }
      datapointState {
        id
        description
      }
    }
  }
  ${CHART_CONFIGURATION_FIELDS}
`;

export const GENES_TEST_BIOMARKERS_CONNECTION_QUERY = gql`
  query GENES_TEST_BIOMARKERS_CONNECTIONS_QUERY(
    $userId: ID!
    $userResultId: ID
    $userTestId: ID
    $first: Int!
    $after: String
    $search: String
    $orderBy: String
    $includeModifiers: Boolean
    $testMode: Boolean
    $organisationId: ID!
  ) {
    userDatapointConnections(
      userId: $userId
      userResultId: $userResultId
      userTestId: $userTestId
      after: $after
      first: $first
      search: $search
      orderBy: $orderBy
      includeModifiers: $includeModifiers
      testMode: $testMode
      organisationId: $organisationId
    ) {
      edges {
        cursor
        node {
          id
          value
          riskAllele
          datapoint {
            id
            name
            chartType
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const TEST_BIOMARKERS_CONNECTION_QUERY = gql`
  query TEST_BIOMARKERS_CONNECTIONS_QUERY(
    $userId: ID!
    $userResultId: ID
    $userTestId: ID
    $first: Int!
    $after: String
    $search: String
    $orderBy: String
    $includeModifiers: Boolean
    $testMode: Boolean
    $organisationId: ID!
  ) {
    userDatapointConnections(
      userId: $userId
      userResultId: $userResultId
      userTestId: $userTestId
      after: $after
      first: $first
      search: $search
      orderBy: $orderBy
      includeModifiers: $includeModifiers
      testMode: $testMode
      organisationId: $organisationId
      selectRelated: [
        "datapoint_state"
        "datapoint__presentation_configuration"
        "datapoint__presentation_configuration__terminology"
        "datapoint__presentation_configuration__override_range"
        "datapoint__presentation_configuration__colour_scheme"
      ]
    ) {
      edges {
        cursor
        node {
          ...UserDatapointFieldsForCot
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${USER_DATAPOINT_FIELDS_FOR_COT}
`;

export const RELATED_RESULTS_QUERY = gql`
  query RELATED_RESULTS_QUERY($organisationId: ID!, $userDatapointId: ID!, $userId: ID!) {
    userDatapoint(
      organisationId: $organisationId
      userDatapointId: $userDatapointId
      userId: $userId
    ) {
      id
      uploaded
      results {
        ...UserResultFieldsTruncated
        isHidden
        resultState {
          id
          name
        }
        chartConfiguration {
          ...ChartConfigurationFields
        }
      }
    }
  }
  ${CHART_CONFIGURATION_FIELDS}
  ${USER_RESULT_FIELDS_TRUNCATED}
`;

export const USER_DATAPOINTS_QUERY = gql`
  query UserDatapointsQuery($datapointId: ID!, $cot: Boolean, $userId: ID!, $organisationId: ID!) {
    userDatapoints(
      datapointId: $datapointId
      cot: $cot
      userId: $userId
      organisationId: $organisationId
    ) {
      ...UserDatapointFieldsForCot
    }
  }
  ${USER_DATAPOINT_FIELDS_FOR_COT}
`;

export const RELATED_GENES_QUERY = gql`
  query RelatedGenesQuery($organisationId: ID!, $userId: ID!, $userResultId: ID!) {
    relatedGenes: userDatapoints(
      userId: $userId
      organisationId: $organisationId
      userResultId: $userResultId
      genesRelated: true
      includeModifiers: true
      userResultIdForRiskAllele: $userResultId
      testMode: true
    ) {
      id
      datapoint {
        id
        name
        chartType
      }
      riskAllele(userResultId: $userResultId)
      value
    }
  }
`;

export const RELATED_RESEARCH_QUERY = gql`
  query RelatedResearchQuery($userId: ID!, $userResultId: ID!, $organisationId: ID!) {
    userResult(userId: $userId, userResultId: $userResultId, organisationId: $organisationId) {
      id
      research {
        id
        name
        url
      }
    }
  }
`;

export const EXPERIENCED_SYMPTOMS_QUERY = gql`
  query ExperiencedSymptomsQuery($userId: ID!, $userResultId: ID!, $organisationId: ID!) {
    userResult(userId: $userId, userResultId: $userResultId, organisationId: $organisationId) {
      id
      experiencedSymptoms {
        id
        question {
          id
          text
        }
        answerText
        answerScore
      }
    }
  }
`;

export const NOT_EXPERIENCED_SYMPTOMS_QUERY = gql`
  query NotExperiencedSymptomsQuery($userId: ID!, $userResultId: ID!, $organisationId: ID!) {
    userResult(userId: $userId, userResultId: $userResultId, organisationId: $organisationId) {
      id
      notExperiencedSymptoms {
        id
        question {
          id
          text
        }
        answerText
        answerScore
      }
    }
  }
`;

export const RELEASE_USER_TEST_MUTATION = gql`
  mutation ReleaseUserTestMutation(
    $userTestId: ID!
    $sendNotification: Boolean
    $organisationId: ID!
  ) {
    releaseUserTest(
      userTestId: $userTestId
      sendNotification: $sendNotification
      organisation: $organisationId
    ) {
      success
    }
  }
`;

export const USER_RESULTS_QUERY = gql`
  query UserResultsQuery($resultId: ID!, $cot: Boolean, $userId: ID!, $organisationId: ID!) {
    userResults(organisationId: $organisationId, resultId: $resultId, cot: $cot, userId: $userId) {
      ...UserResultFieldsForCot
    }
  }
  ${USER_RESULT_FIELDS_FOR_COT}
`;

export const GET_DISPLAY_GROUPS = gql`
  query GetDisplayGroups($userId: ID!, $userTestId: ID!, $organisationId: ID!) {
    userTest(userId: $userId, userTestId: $userTestId, organisationId: $organisationId) {
      id
      resultDisplayGroups {
        id
        name
        description
      }
    }
  }
`;

export const PATIENT_USER_TEST_FIELDS = gql`
  fragment PatientUserTestFields on UserTestType {
    id
    earliestDateExtracted
    created
    createdDateFormatted
    sampleCollectedDateFormatted
    reviewStatus
    name
    slug
    user {
      id
      email
    }
    isGenesTest
    testProduct {
      id
      paymentIsoCode
      organisation
      name
      slug
      truncatedName
      productFamily {
        id
        name
        familyColour
      }
    }
  }
`;

export const PATIENT_ORDERS_IN_PROGRESS_FIELDS = gql`
  fragment PatientOrdersInProgressFields on OrderTestItemType {
    id
    resultsReceivedByProvider
    sampleReceivedByProvider
    sampleReceivedByLab
    nameInBasket
    registered
    testSentToUser
    userTestIds
    product {
      id
      paymentIsoCode
      organisation
      listingVisibility
    }
    order {
      id
      derivedPaymentStatus
    }
  }
`;

export const PATIENT_ORDERS_AND_TESTS = gql`
  query PatientOrdersAndTests(
    $userId: ID!
    $allTests: Boolean!
    $includeUnapproved: Boolean!
    $organisationId: ID!
  ) {
    userTests(
      userId: $userId
      allTests: $allTests
      includeUnapproved: $includeUnapproved
      organisationId: $organisationId
    ) {
      id
      name
      reviewStatus
      externalSourceTest
      createdDateFormatted
      displayOrderId
      displayPaymentStatus
    }
    ordersInProgress(userId: $userId, organisationId: $organisationId) {
      ...PatientOrdersInProgressFields
      hasUserReport
    }
  }
  ${PATIENT_ORDERS_IN_PROGRESS_FIELDS}
`;

export const PATIENT_TESTS = gql`
  query PatientTests(
    $userId: ID!
    $allTests: Boolean!
    $includeUnapproved: Boolean!
    $organisationId: ID!
    $userTestId: ID!
  ) {
    userTests(
      userId: $userId
      allTests: $allTests
      includeUnapproved: $includeUnapproved
      organisationId: $organisationId
    ) {
      id
      name
      reviewStatus
      externalSourceTest
      createdDateFormatted
      displayOrderId
      displayPaymentStatus
    }
    userTest(userId: $userId, userTestId: $userTestId, organisationId: $organisationId) {
      ...PatientUserTestFields
      externalSourceTest
      displayOrderId
      displayPaymentStatus
      resultsBlockedByAccountOnHold
    }
    ordersInProgress(userId: $userId, organisationId: $organisationId) {
      ...PatientOrdersInProgressFields
      hasUserReport
    }
  }
  ${PATIENT_ORDERS_IN_PROGRESS_FIELDS}
  ${PATIENT_USER_TEST_FIELDS}
`;

export const RESEARCH_RELATED_TO_USER_DATAPOINT_QUERY = gql`
  query RelatedResearchQuery($organisationId: ID!, $userId: ID!, $userDatapointId: ID!) {
    userDatapoint(
      organisationId: $organisationId
      userId: $userId
      userDatapointId: $userDatapointId
    ) {
      id
      datapointState {
        id
        research {
          id
          name
          url
        }
      }
    }
  }
`;
